import { OpenHouse, PropertyTypeEnum } from '@engelvoelkersamericas/api';
import { SQ_FT_TO_M2 } from '@util/constants';
import dayjs from 'dayjs';
import { MlsComplianceData } from 'mobx/stores/app-store';

export enum OPEN_HOUSE_TYPE {
  BROKER = 'broker',
  LIVESTREAM_BROKER = 'livestream broker',
  OFFICE = 'office',
  PUBLIC = 'public',
}

// Convert number to string with comma separator
export const formatNumberToStringWithCommas = (num: number) => {
  if (typeof num === 'undefined' || isNaN(num)) {
    return '';
  }
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatStringWithCommasToNumber = (val: string) => {
  if (typeof val === 'undefined' && typeof val !== 'string') {
    return 0;
  }
  return Number(val.replace(/,/g, ''));
};

// Convert square feet to square meters to a string with comma separator
export const convertSquareFtToSquareM = (squareFeet: number): string => {
  const squareMeters = Math.round(squareFeet * SQ_FT_TO_M2);
  return formatNumberToStringWithCommas(squareMeters);
};

export const buildListUrl = (params: {
  StreetNumber?: string | null;
  StreetName?: string | null;
  City?: string | null;
  StateOrProvince?: string | null;
  ZipCode?: string | null;
  OriginatingSystemName?: string | null;
  ListingId?: string | null;
}): string => {
  let {
    StreetNumber,
    StreetName,
    City,
    StateOrProvince,
    ZipCode,
    OriginatingSystemName,
    ListingId,
  } = params;

  if (ListingId) {
    ListingId = ListingId.replace(/-/g, '$');
  }

  let url = '/properties/our-listings/';

  const elements = [
    StreetNumber,
    StreetName,
    City,
    StateOrProvince,
    ZipCode,
    OriginatingSystemName,
    ListingId,
  ];

  const filteredElements = elements.filter((el) => el);

  if (filteredElements.length === 0) {
    throw new Error('Cannot build listing URL');
  }

  url += encodeURIComponent(filteredElements.join('-'));

  return url;
};

export const buildDevelopmentUrl = (params: {
  OriginatingSystemName?: string;
  ListingId?: string;
}): string | undefined => {
  if (!params) {
    return undefined;
  }

  let { OriginatingSystemName, ListingId } = params;

  if (ListingId) {
    ListingId = ListingId.replace(/-/g, '$');
  }

  if (!OriginatingSystemName || !ListingId) {
    // these are required for the api call
    return undefined;
  }

  let url = '/explore/development-services/listings/';

  const elements = [OriginatingSystemName, ListingId];

  const filteredElements = elements.filter((el) => el);

  if (filteredElements.length === 0) {
    return undefined;
  }

  url += encodeURIComponent(filteredElements.join('-'));

  return url;
};

export const processListingStatus = (
  openHouse: OpenHouse[] | null,
  status: string,
  propertyType: string,
): string | null => {
  // Open House
  if (openHouse && openHouse.length > 0 && status === 'Active') {
    //boolean check if any item in the openHouse array has a OpenHouseEndTime that is greater than the current time
    const activeOpenHouseArr = openHouse
      ?.filter((openHouseArg) => {
        let { OpenHouseEndTime, OpenHouseStatus, OpenHouseType } = openHouseArg;
        if (OpenHouseStatus === 'Deleted') return false;
        /* If the OpenHouseType is of type office, livestream broker, or broker which means private, then dont return open house status */
        if (
          OpenHouseType != null &&
          (OpenHouseType.toLowerCase() === OPEN_HOUSE_TYPE.BROKER ||
            OpenHouseType.toLowerCase() === OPEN_HOUSE_TYPE.LIVESTREAM_BROKER ||
            OpenHouseType.toLowerCase() === OPEN_HOUSE_TYPE.OFFICE)
        ) {
          return false;
        }

        const endDateTime = dayjs(OpenHouseEndTime);
        // Get the current date and time
        const currentDateTime = dayjs();
        // Check if current time is less than endTime
        return endDateTime.isAfter(currentDateTime);
      })
      .sort((a, b) => {
        const startTimeA = dayjs(a.OpenHouseStartTime);
        const startTimeB = dayjs(b.OpenHouseStartTime);
        return startTimeA.diff(startTimeB);
      });

    if (activeOpenHouseArr.length > 0) {
      const { OpenHouseStartTime, OpenHouseEndTime } = activeOpenHouseArr[0]!;

      const startDateTimeDateTime = dayjs(removeTimeZone(OpenHouseStartTime));
      const endDateTimeDateTime = dayjs(removeTimeZone(OpenHouseEndTime));

      const formattedDay = startDateTimeDateTime.format('ddd M/D'); // Day of the week
      const formattedStartTime = startDateTimeDateTime.format('h:mma');
      const formattedEndTime = endDateTimeDateTime.format('h:mma');
      return `Open House: ${formattedDay}, ${formattedStartTime}-${formattedEndTime}`;
    }
  }

  function removeTimeZone(dateStr: string) {
    if (dateStr.includes('T')) {
      let lastIndex = dateStr.lastIndexOf('+');
      if (lastIndex > 0) {
        return dateStr.slice(0, lastIndex);
      }
      let dateArr = dateStr.split('T');
      let timePart = dateArr[1];
      if (timePart?.lastIndexOf('-')) {
        lastIndex = dateStr.lastIndexOf('-');
        if (lastIndex > 0) {
          return dateStr.slice(0, lastIndex);
        }
      }
    }
    return dateStr;
  }

  // Priority of Statuses
  switch (status) {
    case 'Active':
      return null;
    case 'Active Under Contract':
      return 'Under Contract';
    case 'Closed':
      if (propertyType === 'Residential Lease' || propertyType === 'Commercial Lease') {
        return 'Leased';
      } else {
        return 'Sold';
      }
    default:
      return status;
  }
};

export const displayIcon = (isIcon) => {
  return isIcon !== undefined ? isIcon : true;
};

export const getExchangeRate = (
  exchangeRates: Record<string, number>,
  baseCurrency: string,
  targetCurrency: string,
) => {
  if (!exchangeRates || !exchangeRates[targetCurrency] || !exchangeRates[baseCurrency]) {
    return 1;
  }

  return exchangeRates[targetCurrency] / exchangeRates[baseCurrency];
};

/** Converts a value to another currency */
export const convertCurrency = (
  val: number,
  exchangeRates: Record<string, number>,
  baseCurrency: string,
  targetCurrency: string,
) => {
  const rate = getExchangeRate(exchangeRates, baseCurrency, targetCurrency);
  return val * rate;
};

export interface GetListingPriceToDisplayParams {
  standardStatus: string | null;
  originatingSystemName: string | null;
  listPrice?: number | null;
  closePrice?: number | null;
  closePriceToDisplay?: string | null;
  propertyType?: PropertyTypeEnum;
  leaseAmount?: number | null;
  leaseAmountFrequency?: string | null;
}

export function getListingPriceToDisplay({
  standardStatus,
  originatingSystemName,
  listPrice,
  closePrice,
  closePriceToDisplay = 'ClosePrice',
  propertyType,
  leaseAmount,
  leaseAmountFrequency,
}: GetListingPriceToDisplayParams): number {
  let price = listPrice;
  if (originatingSystemName === 'OttawaON') {
    price = listPrice;
  } else if (originatingSystemName === 'EVAMLS' && standardStatus === 'Closed') {
    price = closePriceToDisplay === 'ClosePrice' ? closePrice || listPrice : listPrice;
  } else if (standardStatus === 'Closed') {
    price = closePrice || listPrice;
  } else if (
    propertyType &&
    leaseAmount &&
    leaseAmountFrequency &&
    PropertyTypeEnum.ResidentialLease === propertyType
  ) {
    price = leaseAmount;
  }
  return price ?? 0;
}

export interface GetListingCurrencyParams {
  originatingSystemName: string | null;
  country: string;
  listingCurrency: string | null;
  mlsComplianceData: MlsComplianceData | null;
}

export function getListingCurrency({
  originatingSystemName,
  country,
  listingCurrency,
  mlsComplianceData,
}: GetListingCurrencyParams): string {
  // 1. Check if the currency code is already provided
  if (listingCurrency) {
    return listingCurrency;
  }

  // 2. Check if the MLS compliance data is available
  if (originatingSystemName) {
    const osnDefaultCurrency = mlsComplianceData?.[originatingSystemName]?.defaultCurrency;
    if (osnDefaultCurrency) {
      return osnDefaultCurrency;
    }
  }

  // 3. Hardcoded rules that always apply for for certain countries or MLS systems
  if (country === 'CA' || (originatingSystemName && ['CENTRIS'].includes(originatingSystemName))) {
    return 'CAD';
  } else if (
    country === 'MX' ||
    (originatingSystemName && ['LosCabos'].includes(originatingSystemName))
  ) {
    return 'MXN';
  }

  // 4. Default to USD as the final fallback
  return 'USD';
}
